import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { GTDialogBody, GTDialogFooter, GTDialogHeading } from '@/components/ui/gt-dialog'
import { useSelectedCalendars } from '@/services/api/events.hooks'
import { AlertTriangle, ArrowRight, CalendarDays, Loader2, Repeat2 } from 'lucide-react'
import {
    TGoalStartingPoint,
    TPlanningCommitment,
    formatWeeklyTime,
    hasCapacityForGoal,
    loadPlanningContext,
} from './startingPoint'

interface StartingPointPanelProps {
    title: string
    goalId?: string
    capacityLabel: string
    initialBaseline?: string
    initialStartingPoint?: TGoalStartingPoint
    onBack?: () => void
    onCancel?: () => void
    onConfirm: (startingPoint: TGoalStartingPoint) => void
}

const StartingPointPanel = ({
    title,
    goalId,
    capacityLabel,
    initialBaseline = '',
    initialStartingPoint,
    onBack,
    onCancel,
    onConfirm,
}: StartingPointPanelProps) => {
    const { selectedCalendars, isLoading: calendarsLoading } = useSelectedCalendars()
    const [baseline, setBaseline] = useState(initialStartingPoint?.baseline ?? initialBaseline)
    const [commitments, setCommitments] = useState<TPlanningCommitment[]>(initialStartingPoint?.commitments ?? [])
    const [loadStatus, setLoadStatus] = useState<'loading' | 'success' | 'error'>(
        initialStartingPoint?.contextLoadFailed ? 'error' : initialStartingPoint ? 'success' : 'loading'
    )
    const [calendarStatus, setCalendarStatus] = useState<
        'loading' | 'ready' | 'partial' | 'unavailable' | 'excluded' | 'none'
    >(initialStartingPoint?.contextLoadFailed ? 'unavailable' : initialStartingPoint ? 'ready' : 'loading')
    const [calendarIssues, setCalendarIssues] = useState<string[]>([])
    const [retryNonce, setRetryNonce] = useState(0)

    const selectionKey = JSON.stringify(
        selectedCalendars.map((account) => ({
            account_id: account.account_id,
            calendar_ids: account.calendars.map((calendar) => calendar.calendar_id).sort(),
        }))
    )

    useEffect(() => {
        if (calendarsLoading || (initialStartingPoint && (!initialStartingPoint.contextLoadFailed || retryNonce === 0)))
            return
        const controller = new AbortController()
        setLoadStatus('loading')
        setCalendarStatus('loading')
        setCalendarIssues([])
        setCommitments([])
        const calendars = JSON.parse(selectionKey) as Array<{ account_id: string; calendar_ids: string[] }>
        void loadPlanningContext(title, goalId, calendars, controller.signal)
            .then((context) => {
                setCommitments(context.commitments)
                setCalendarStatus(context.calendarStatus)
                setCalendarIssues(context.calendarIssues)
                setLoadStatus('success')
            })
            .catch((requestError: unknown) => {
                if (!controller.signal.aborted && (requestError as { name?: string })?.name !== 'CanceledError') {
                    setCommitments([])
                    setCalendarIssues([])
                    setCalendarStatus('unavailable')
                    setLoadStatus('error')
                }
            })
        return () => controller.abort()
    }, [calendarsLoading, goalId, initialStartingPoint, retryNonce, selectionKey, title])

    const hasRoomForGoal = useMemo(
        () => loadStatus === 'error' || hasCapacityForGoal(capacityLabel, commitments),
        [capacityLabel, commitments, loadStatus]
    )
    const recurringCommitments = commitments.filter((commitment) => commitment.source === 'recurring')
    const calendarCommitments = commitments.filter((commitment) => commitment.source === 'calendar')
    const calendarWasRead = calendarStatus === 'ready' || calendarStatus === 'partial'

    const setCommitment = (id: string, update: Partial<TPlanningCommitment>) =>
        setCommitments((current) =>
            current.map((commitment) => (commitment.id === id ? { ...commitment, ...update } : commitment))
        )

    const confirm = () => {
        const startingPoint = {
            baseline: baseline.trim(),
            commitments: loadStatus === 'error' ? [] : commitments,
            contextLoadFailed: loadStatus === 'error',
        }
        onConfirm(startingPoint)
    }

    const warning =
        calendarStatus === 'partial'
            ? calendarIssues[0] ?? 'Some calendars could not load.'
            : calendarStatus === 'unavailable'
            ? calendarIssues[0] ?? 'Calendar needs attention.'
            : calendarStatus === 'excluded'
            ? 'Calendar not included.'
            : calendarStatus === 'none'
            ? 'No calendar connected.'
            : null

    const commitmentRows = (items: TPlanningCommitment[]) => (
        <div className="divide-y divide-border/70">
            {items.map((commitment) => (
                <div key={`${commitment.source}-${commitment.id}`} className="flex items-center gap-3 py-2.5">
                    <Checkbox
                        checked={commitment.included}
                        onCheckedChange={(checked) => setCommitment(commitment.id, { included: checked === true })}
                        aria-label={`Include ${commitment.title} in planning`}
                    />
                    {commitment.source === 'recurring' ? (
                        <Repeat2 className="size-4 shrink-0 text-muted-foreground" />
                    ) : (
                        <CalendarDays className="size-4 shrink-0 text-muted-foreground" />
                    )}
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-body-sm font-medium text-foreground">{commitment.title}</p>
                        <p className="text-body-sm text-muted-foreground">
                            {formatWeeklyTime(commitment.minutesPerWeek)} / week
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setCommitment(commitment.id, { movesGoal: !commitment.movesGoal })}
                        aria-pressed={commitment.movesGoal}
                        aria-label={`${commitment.title}: ${
                            commitment.movesGoal ? 'Moves this goal' : 'Other commitment'
                        }`}
                        className={
                            commitment.movesGoal
                                ? 'rounded-sm bg-success/15 px-2 py-1 text-label-sm text-success'
                                : 'rounded-sm bg-muted px-2 py-1 text-label-sm text-muted-foreground'
                        }
                    >
                        {commitment.movesGoal ? 'Moves this goal' : 'Other commitment'}
                    </button>
                </div>
            ))}
        </div>
    )

    return (
        <>
            <GTDialogBody className="gap-5">
                <GTDialogHeading
                    title="Your starting point"
                    subtitle="Fix what is off. The plan is drafted from this."
                />

                <div className="space-y-2">
                    <label
                        htmlFor="goal-baseline"
                        className="text-label-md uppercase tracking-wider text-muted-foreground"
                    >
                        Where you are now
                    </label>
                    <textarea
                        id="goal-baseline"
                        value={baseline}
                        onChange={(event) => setBaseline(event.target.value)}
                        rows={2}
                        placeholder="What have you tried? What can you already do?"
                        className="flex w-full resize-none rounded-lg border border-input bg-transparent px-3 py-2.5 text-body-md leading-relaxed placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                </div>

                <section className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-label-md uppercase tracking-wider text-muted-foreground">
                            Existing commitments
                        </span>
                        {calendarStatus === 'loading' && (
                            <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                        )}
                    </div>
                    {loadStatus === 'success' && (
                        <div className="space-y-4">
                            <div className="space-y-1">
                                <div>
                                    <p className="text-body-sm font-medium text-foreground">Recurring tasks</p>
                                    <p className="text-body-sm text-muted-foreground">
                                        Goal work can be reused as a goal hook.
                                    </p>
                                </div>
                                {recurringCommitments.length > 0 ? (
                                    commitmentRows(recurringCommitments)
                                ) : (
                                    <p className="text-body-sm text-muted-foreground">No recurring tasks found.</p>
                                )}
                            </div>
                            <div className="space-y-1">
                                <div>
                                    <p className="text-body-sm font-medium text-foreground">Calendar events</p>
                                    <p className="text-body-sm text-muted-foreground">
                                        Planning context only. Events aren&apos;t added, copied, linked, or rescheduled.
                                    </p>
                                </div>
                                {calendarCommitments.length > 0 ? (
                                    commitmentRows(calendarCommitments)
                                ) : calendarWasRead ? (
                                    <p className="text-body-sm text-muted-foreground">No calendar events found.</p>
                                ) : null}
                            </div>
                        </div>
                    )}
                    {loadStatus === 'error' ? (
                        <div className="flex items-center gap-2 text-body-sm text-gt-gold">
                            <AlertTriangle className="size-3.5" />
                            <span>Weekly context could not load.</span>
                            <Button variant="ghost" size="sm" onClick={() => setRetryNonce((value) => value + 1)}>
                                Retry
                            </Button>
                        </div>
                    ) : warning ? (
                        <p className="flex items-center gap-1.5 text-body-sm text-gt-gold">
                            <AlertTriangle className="size-3.5" />
                            {warning}
                        </p>
                    ) : null}
                    {loadStatus === 'success' && !hasRoomForGoal && (
                        <p className="text-body-sm text-gt-gold">Make room before drafting.</p>
                    )}
                </section>
            </GTDialogBody>
            <GTDialogFooter
                start={
                    onBack ? (
                        <Button variant="ghost" size="sm" onClick={onBack}>
                            Back
                        </Button>
                    ) : undefined
                }
                onCancel={onCancel}
                cancelLabel="Discard"
                onConfirm={confirm}
                confirmDisabled={loadStatus === 'loading' || !hasRoomForGoal}
                confirmLabel={
                    <>
                        <span>{loadStatus === 'error' ? 'Draft without my week' : 'Draft plan'}</span>
                        <ArrowRight className="size-4" />
                    </>
                }
            />
        </>
    )
}

export default StartingPointPanel
